import { useState, useCallback, useEffect } from 'react';

import {
  alpha,
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import { Close, Search } from '@material-ui/icons';

import NewMessagesContainer from 'components/NewMessagesContainer';
import { useAppDispatch, useAppSelector } from 'store';
import {
  mensagensSelector,
  resetIdMensagemPesquisada,
  setCursorAConsumir,
  setBeforeCursorAConsumir,
  setAfterCursorAConsumir,
  resetAtendimentoFilaCursorAConsumir,
  resetMensagensAtendimentoNaFila,
  fetchMensagensFinalizadasAtendimentoFila,
} from 'store/reducers/mensagens';

const useStyles = makeStyles(({ palette, breakpoints, spacing }) => ({
  mainGrid: {
    height: '100%',
    minWidth: '400px',
    width: '100%',
    margin: 0,
    backgroundColor: alpha(palette.background.tertiary, 0.5),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [breakpoints.down('sm')]: {
      minWidth: '300px',
      padding: spacing(0, 2),
    },
    [breakpoints.down('xs')]: {
      minWidth: '250px',
      padding: spacing(0, 1),
    },
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing(0.5),
    padding: spacing(0, 1),
    height: 60,
    backgroundColor: palette.background.paper,
    borderBottom: `1px solid ${palette.divider}`,
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,

    [breakpoints.down(1080)]: {
      display: 'none',
    },
  },
  iconButton: {},
}));

interface RightGridMensagensAtendimentoProps {
  atendimentoId: string;
  contactName: string;
  searchQuery?: string;
  openSearch?: boolean;
  onToggleSearch?: () => void;
  onClose?: () => void;
}

export default function RightGridMensagensAtendimento({
  atendimentoId,
  contactName,
  searchQuery,
  openSearch,
  onToggleSearch,
  onClose,
}: RightGridMensagensAtendimentoProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const {
    atendimentoFilaCursorAConsumir,
    mensagensAtendimentoNaFila,
    mensagemIdPesquisada,
    mensagensAtendimentoNaFilaCursor,
    loadingMensagensAtendimentoNaFila,
  } = useAppSelector(mensagensSelector);

  const [initialLoadingMessages, setInitialLoadingMessages] = useState(false);

  const handleResetLista = useCallback(() => {
    if (atendimentoId) {
      setInitialLoadingMessages(true);
      dispatch(resetIdMensagemPesquisada());
      dispatch(resetAtendimentoFilaCursorAConsumir());
      dispatch(resetMensagensAtendimentoNaFila());
      dispatch(
        fetchMensagensFinalizadasAtendimentoFila({
          atendimentoId,
        }),
      )
        .unwrap()
        .then(data => {
          const { cursor } = data;
          dispatch(
            setCursorAConsumir({ ...cursor, type: 'atendimento-na-fila' }),
          );
        })
        .finally(() => {
          setInitialLoadingMessages(false);
        });
    }
  }, [atendimentoId, dispatch]);

  const fetchNextMensagensFinalizadas = useCallback(() => {
    const { afterCursor: afterAConsumir } =
      atendimentoFilaCursorAConsumir[0] || {};
    const { afterCursor } = mensagensAtendimentoNaFilaCursor;

    if (atendimentoId) {
      if (mensagemIdPesquisada) {
        if (afterAConsumir) {
          dispatch(
            fetchMensagensFinalizadasAtendimentoFila({
              atendimentoId,
              cursor: { afterCursor: afterAConsumir },
            }),
          )
            .unwrap()
            .then(data => {
              const { afterCursor } = data.cursor;

              dispatch(
                setAfterCursorAConsumir({
                  afterCursor,
                  type: 'atendimento-na-fila',
                }),
              );
            });
        }
      } else {
        dispatch(
          fetchMensagensFinalizadasAtendimentoFila({
            atendimentoId,
            cursor: { afterCursor },
          }),
        )
          .unwrap()
          .then(data => {
            const { afterCursor } = data.cursor;

            dispatch(
              setAfterCursorAConsumir({
                afterCursor,
                type: 'atendimento-na-fila',
              }),
            );
          });
      }
    }
  }, [
    atendimentoId,
    dispatch,
    atendimentoFilaCursorAConsumir,
    mensagemIdPesquisada,
    mensagensAtendimentoNaFilaCursor,
  ]);

  const fetchPreviousMensagensFinalizadas = useCallback(() => {
    const { beforeCursor: beforeAConsumir } =
      atendimentoFilaCursorAConsumir[0] || {};
    const { beforeCursor } = mensagensAtendimentoNaFilaCursor;

    if (atendimentoId) {
      if (mensagemIdPesquisada) {
        if (beforeAConsumir) {
          dispatch(
            fetchMensagensFinalizadasAtendimentoFila({
              atendimentoId,
              cursor: { beforeCursor: beforeAConsumir },
            }),
          )
            .unwrap()
            .then(data => {
              const { beforeCursor } = data.cursor;

              dispatch(
                setBeforeCursorAConsumir({
                  beforeCursor,
                  type: 'atendimento-na-fila',
                }),
              );
            });
        }
      } else {
        dispatch(
          fetchMensagensFinalizadasAtendimentoFila({
            atendimentoId,
            cursor: { beforeCursor },
          }),
        )
          .unwrap()
          .then(data => {
            const { beforeCursor } = data.cursor;

            dispatch(
              setBeforeCursorAConsumir({
                beforeCursor,
                type: 'atendimento-na-fila',
              }),
            );
          });
      }
    }
  }, [
    atendimentoId,
    dispatch,
    atendimentoFilaCursorAConsumir,
    mensagemIdPesquisada,
    mensagensAtendimentoNaFilaCursor,
  ]);

  useEffect(() => {
    handleResetLista();
  }, [atendimentoId, handleResetLista]);

  return (
    <Box className={classes.mainGrid}>
      {(onToggleSearch || onClose) && (
        <Box className={classes.actionBar}>
          {onToggleSearch && (
            <IconButton
              onClick={onToggleSearch}
              title="Pesquisar mensagens"
              color={openSearch ? 'primary' : 'default'}
              className={classes.iconButton}
            >
              <Search fontSize="medium" />
            </IconButton>
          )}
          {onClose && (
            <IconButton
              onClick={onClose}
              title="Fechar"
              className={classes.iconButton}
            >
              <Close fontSize="medium" />
            </IconButton>
          )}
        </Box>
      )}

      {initialLoadingMessages && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress color="primary" size={35} />
        </Box>
      )}

      {!initialLoadingMessages && (
        <NewMessagesContainer
          messages={mensagensAtendimentoNaFila}
          next={fetchNextMensagensFinalizadas}
          previous={fetchPreviousMensagensFinalizadas}
          hasAfterCursor={
            mensagemIdPesquisada
              ? Boolean(atendimentoFilaCursorAConsumir[0]?.afterCursor)
              : Boolean(mensagensAtendimentoNaFilaCursor.afterCursor)
          }
          hasBeforeCursor={
            mensagemIdPesquisada
              ? Boolean(atendimentoFilaCursorAConsumir[0]?.beforeCursor)
              : Boolean(mensagensAtendimentoNaFilaCursor.beforeCursor)
          }
          onResetLista={handleResetLista}
          loading={loadingMensagensAtendimentoNaFila}
          scrollableTargetId="tabPanelConversa"
          actions={false}
          endMessage={`Este é o início da sua conversa com ${contactName}`}
          historicoAtendimento
          searchQuery={searchQuery}
        />
      )}
    </Box>
  );
}
